import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Square, Star, Volume2 } from 'lucide-react';

import { AudioBridge } from '../audio/bridge';
import { keyGesture } from '../audio/gesture.ts';
import { claim, hold } from '../audio/oneVoice.ts';
import { type WavCache } from '../audio/replay.ts';
import { t, type Message, type PlainKey } from '../i18n/t';
import { HearIt, type SpeakTarget } from './HearIt';
import { LABELS, SayWithMe as Controller, statusFor, type Playback, type SayWithMePhase } from './sayWithMe.ts';

const playback: Playback = {
  url: (wav) => URL.createObjectURL(new Blob([wav.buffer as ArrayBuffer], { type: 'audio/wav' })),
  revoke: (url) => URL.revokeObjectURL(url),
  audio: (url) => new Audio(url),
  claim,
  hold,
};

/**
 * The star's pop, scaled by `--tutor-intensity` and skipped at 0, which
 * `app.css` sets under reduced motion: then the star simply appears (§7).
 * Read from the computed style because this card is outside `CharacterFace`,
 * so the root value is the one that applies.
 */
function pop(el: Element | null) {
  if (!el) return;
  const k = parseFloat(getComputedStyle(el).getPropertyValue('--tutor-intensity')) || 0;
  if (k > 0) {
    el.animate([{ transform: `scale(${1 + 0.6 * k})` }, { transform: 'scale(1)' }], {
      duration: 350,
      easing: 'ease-out',
    });
  }
}

/**
 * "Hear it" and "Say it with me", side by side on a kid's card, with the one
 * line under them (kid-practice §5, §7). The Hear it target is the caller's:
 * a practice example, or a book's page. The child hears the tutor, records
 * themselves, and hears the recording straight back; nothing is sent,
 * stored or scored (§5.4). Kid register only (§5.6).
 */
export function SayWithMe({ target, cache }: { target: SpeakTarget; cache?: WavCache<string> }) {
  const [phase, setPhase] = useState<SayWithMePhase>('rest');
  const [error, setError] = useState<Message | null>(null);
  // Remounts the "That was you!" line on each loop, so the live region
  // announces a repeat and the star pops again.
  const [loops, setLoops] = useState(0);
  const controller = useRef<Controller | null>(null);

  useEffect(() => {
    const c = new Controller(
      (onPcm, onEnded) => new AudioBridge(onPcm, onEnded, () => {}),
      playback,
      (next, e) => {
        setPhase(next);
        setError(e);
        if (next === 'done') setLoops((n) => n + 1);
      },
    );
    controller.current = c;
    return () => {
      c.dispose();
      controller.current = null;
    };
  }, []);

  const onKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
    if (keyGesture(e.key, 'down', e.repeat) === 'down') controller.current?.gesture('down');
  }, []);

  const status = statusFor(phase, error);
  const listening = phase === 'opening' || phase === 'recording';
  const icon =
    phase === 'opening' ? (
      <Loader2 aria-hidden className="size-5 animate-spin" />
    ) : phase === 'recording' ? (
      <Square aria-hidden className="size-5 fill-current" />
    ) : phase === 'playing' ? (
      <Volume2 aria-hidden className="size-5" />
    ) : (
      <Mic aria-hidden className="size-5" />
    );

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="grid grid-cols-2 items-start gap-2">
        <HearIt target={target} cache={cache} className="w-full" blocked={listening} />
        <button
          type="button"
          // `touch-none` and no callout: say-it-back amendments 10–12. The
          // gesture is a tap, but a thumb that drifts still must not scroll
          // the card or raise the iOS menu mid-recording.
          className={`btn mt-1 min-h-(--hit-min) w-full gap-2 touch-none select-none [-webkit-touch-callout:none] ${phase === 'recording' ? 'btn-error' : 'btn-outline'}`}
          aria-pressed={phase === 'recording'}
          aria-disabled={phase === 'playing'}
          onPointerDown={() => controller.current?.gesture('down')}
          onPointerCancel={() => controller.current?.gesture('cancel')}
          onKeyDown={onKey}
        >
          {icon}
          {/* Every label in one grid cell, so the button keeps its size, and
              centred: "Opening the microphone…" wraps at half a phone. */}
          <span className="grid items-center text-start">
            {[...new Set<PlainKey>(Object.values(LABELS))].map((l) => (
              <span
                key={l}
                aria-hidden={l !== LABELS[phase]}
                className={`col-start-1 row-start-1 ${l === LABELS[phase] ? '' : 'invisible'}`}
              >
                {t(l)}
              </span>
            ))}
          </span>
        </button>
      </div>
      {/* One reserved line, so nothing arriving or going moves the card. */}
      <div aria-live="polite" className="flex min-h-8 items-center justify-center text-sm">
        {status &&
          (status.alert ? (
            <p role="alert" className="text-error">
              {t(status.text)}
            </p>
          ) : (
            <p key={loops} className="flex items-center gap-2 font-medium">
              <Star ref={pop} aria-hidden className="text-warning size-6 fill-current" />
              {t(status.text)}
            </p>
          ))}
      </div>
    </div>
  );
}
